// Pure composition: merges LocationRoutes + OpsLocations + DriverLocations
// pings into the DriverRecord[] shape the Monitoring tab UI consumes.
// Kept side-effect-free so it's unit-testable without React or AppSync.
//
// See useDriverLocationsPoll.js for the DriverRecord typedef.

import { parseUtcDateTime } from './datetime';
import {
  ARRIVAL_SCAN_EVENT,
  LOCATION_STOCKED_EVENT,
} from '../libs/driverActions';
import {
  formatClockTime,
  formatDurationMinutes,
  formatDeltaLabel,
  formatDriverName,
} from './displayFormat';

// Status thresholds (locked for v1 per design doc). Exported so display
// surfaces (e.g. RouteListView's deviation tones) stay in lock-step with the
// status tiers computed here.
export const ON_TRACK_LIMIT_MIN = 5;
export const BEHIND_LIMIT_MIN = 15;

// Display threshold for the map's severe-lateness cue (red driver marker):
// strictly more than this many minutes behind schedule.
export const SEVERELY_LATE_MIN = 30;

export function isSeverelyLate(etaDeltaMin) {
  return (
    etaDeltaMin !== null &&
    etaDeltaMin !== undefined &&
    etaDeltaMin > SEVERELY_LATE_MIN
  );
}

// Canonical ascending sort for RouteStop-shaped objects.
export const byStopIndex = (a, b) => (a.stopIndex || 0) - (b.stopIndex || 0);

function diffMinutes(a, b) {
  if (!a || !b) return null;
  return Math.round((a.getTime() - b.getTime()) / 60000);
}

function deltaTierFor(min) {
  if (min === null || min === undefined) return 'eta';
  if (min <= -1) return 'early';
  if (min <= ON_TRACK_LIMIT_MIN) return 'on-time';
  if (min <= BEHIND_LIMIT_MIN) return 'over';
  return 'late';
}

function statusTierFor(etaDeltaMin) {
  if (etaDeltaMin === null || etaDeltaMin === undefined) return 'on-track';
  if (etaDeltaMin > BEHIND_LIMIT_MIN) return 'off-track';
  if (etaDeltaMin > ON_TRACK_LIMIT_MIN) return 'behind';
  return 'on-track';
}

function lastPingSecondsAgo(timestampStr, nowMs) {
  const t = parseUtcDateTime(timestampStr);
  if (!t) return null;
  return Math.max(0, Math.round((nowMs - t.getTime()) / 1000));
}

// Build a routeStops[] entry by joining a LocationRoutes row with OpsLocations.
// Every clock time the records carry (stocked, scanned, ETA, scheduled
// start, back at depot) names its timezone. Planned windows stay bare —
// they are ranges, and the suffix would double up.
const formatDataTime = (date) => formatClockTime(date, { zone: true });

function buildRouteStop(row, opsLocation, stateOverride, nowMs) {
  const plannedStart = parseUtcDateTime(row.calculatedStartTime);
  const plannedEnd = parseUtcDateTime(row.calculatedEndTime);
  const stocked = parseUtcDateTime(row.stockedTimeUTC);
  const plannedMin =
    plannedStart && plannedEnd
      ? Math.max(0, diffMinutes(plannedEnd, plannedStart))
      : 0;

  let state = stateOverride;
  if (!state) state = stocked ? 'done' : 'pending';

  let delta = null;
  if (state === 'done' && stocked && plannedEnd) {
    delta = diffMinutes(stocked, plannedEnd);
  } else if (state === 'pending' && plannedStart) {
    delta = diffMinutes(new Date(nowMs), plannedStart);
  }

  // Planned-window display prefers the NextMV input window (the authoritative
  // delivery window fed to the optimizer — populated for runs created after
  // the BE deploy) and falls back to the optimizer's calculated stop times.
  // Both nextmv bounds must be present to be used — mixing one nextmv bound
  // with a calculated time can render an inverted window (e.g. 4:00–3:30 PM).
  // The ETA / delta math above always uses calculatedStartTime/End regardless.
  const nextmvStart = parseUtcDateTime(row.nextmvDeliveryWindowStart);
  const nextmvEnd = parseUtcDateTime(row.nextmvDeliveryWindowEnd);
  const hasNextmvWindow = Boolean(nextmvStart && nextmvEnd);
  const windowStart = hasNextmvWindow ? nextmvStart : plannedStart;
  const windowEnd = hasNextmvWindow ? nextmvEnd : plannedEnd;
  let plannedWindow = '—';
  if (windowStart && windowEnd) {
    plannedWindow = `${formatClockTime(windowStart)}–${formatClockTime(
      windowEnd,
    )}`;
  }

  return {
    stopIndex: row.stop,
    stopId: row.id,
    stopName: opsLocation?.prettyName || opsLocation?.id || row.id,
    // Some locations expose a nested coordinates object, others only the flat
    // latitude/longitude scalars (e.g. AMAZON_DCH8) — fall back so either shape
    // yields a plottable point. `??` not `||` so a legitimate 0 isn't dropped.
    latitude: opsLocation?.coordinates?.latitude ?? opsLocation?.latitude,
    longitude: opsLocation?.coordinates?.longitude ?? opsLocation?.longitude,
    type: opsLocation?.type || '',
    state,
    plannedWindow,
    plannedMin,
    // ms timestamps kept so the forecast pass downstream can recompute
    // currentEta without re-parsing strings.
    plannedStartMs: plannedStart ? plannedStart.getTime() : null,
    plannedEndMs: plannedEnd ? plannedEnd.getTime() : null,
    stockedAt: stocked ? formatDataTime(stocked) : undefined,
    // Raw stocked timestamp (ms) so the actual visit order can be compared
    // against the planned stop sequence to flag out-of-sequence stops.
    stockedAtMs: stocked ? stocked.getTime() : null,
    // Set in the post-build pass below; true when this done stop was visited
    // ahead of a lower-numbered stop.
    outOfSequence: false,
    // Set in the post-build pass below; true when this pending stop sits
    // behind the driver's furthest progress (see markSkipped).
    skipped: false,
    // 1-based rank by actual stocking time among this route's done stops;
    // set in the post-build pass. Null until the stop is stocked.
    visitOrder: null,
    // Arrival scan + actual on-site duration — populated in the post-build pass
    // when PackingSlipQuery scan data is supplied (stocked − scanned).
    scannedAtMs: null,
    scannedAt: undefined,
    actualDurationMin: null,
    // Who stocked this stop: the driver on its traffic legs, else the arrival
    // scan's driver. Per stop, not per route — routes are sometimes worked by
    // two drivers at once (e.g. an Illinois half and an Indiana half).
    stockedBy: null,
    stockedByName: undefined,
    // Who scanned into a stop still in progress — nothing is stocked yet, so
    // there is no stocker, but the driver day view must know whose stop it is.
    scannedBy: null,
    // Fields for the traffic-aware drive leg ENDING at this stop — populated
    // in the traffic pass. legEtaMs = departure + predicted drive time.
    driveDurationMin: null,
    staticDurationMin: null,
    trafficDelayMin: null,
    distanceMiles: null,
    legDepartedAtMs: null,
    legEtaMs: null,
    legEtaLabel: undefined,
    legOrigin: null,
    legPolyline: null,
    legDriver: null,
    etaLabel:
      state === 'pending' && plannedStart
        ? `Planned ${formatDataTime(plannedStart)}`
        : undefined,
    delta,
  };
}

// Forecast / risk-tier resolution for pending stops. Carry-forward model:
// every downstream stop inherits the driver's current etaDeltaMin as its
// projected shift. Tier rules follow the product spec:
//   - red    → projected arrival is past the location's closure deadline
//              ("won't make access")
//   - yellow → projected arrival is within ~15 min of closure ("tight")
//   - green  → comfortable cushion
//
// `plannedEndMs` (NextMV's calculated stop end) is the v1 closure-proxy
// because the dedicated "building closes at X" BE query isn't deployed yet.
// When that query lands (absolute timestamp + TZ), store it on the stop as
// `closesAtMs` and prefer it over plannedEndMs here — the rest of the tier
// resolution stays unchanged.
const CLOSURE_TIGHT_MIN = 15;

function reasonForClosure({ minutesUntilClose, delayMin }) {
  if (minutesUntilClose === null) {
    if (delayMin > 0) return `Driver +${delayMin}m late`;
    return 'On track';
  }
  if (minutesUntilClose < 0) {
    return `Won't make access: projected ${formatDurationMinutes(
      minutesUntilClose,
    )} past close`;
  }
  if (minutesUntilClose <= CLOSURE_TIGHT_MIN) {
    return `Tight: ${formatDurationMinutes(
      minutesUntilClose,
    )} before close (driver +${delayMin}m)`;
  }
  if (delayMin > 0) {
    return `On track: +${delayMin}m, ${formatDurationMinutes(
      minutesUntilClose,
    )} of cushion`;
  }
  if (delayMin < 0) return `On track: ${Math.abs(delayMin)}m ahead`;
  return 'On track';
}

// Returns a new stop carrying the forecast fields; non-pending stops (or
// stops with no planned start) pass through unchanged.
function attachForecast(stop, carryForwardDelayMin) {
  if (stop.state !== 'pending') return stop;
  if (stop.plannedStartMs === null) return stop;
  const currentEtaMs = stop.plannedStartMs + carryForwardDelayMin * 60000;

  const tierFor = (mins) => {
    if (mins < 0) return 'red';
    if (mins <= CLOSURE_TIGHT_MIN) return 'yellow';
    return 'green';
  };

  let tier;
  let minutesUntilClose = null;
  if (stop.plannedEndMs !== null) {
    // Closure proxy (see the forecast header comment): the optimizer's
    // planned stop-end. A projected arrival past the planned end means the
    // route is running behind its schedule for this stop — so the forecast
    // list stays meaningful and consistent with the route's status tier,
    // instead of reporting "0 at risk" for plainly off-track routes.
    minutesUntilClose = Math.round((stop.plannedEndMs - currentEtaMs) / 60000);
    tier = tierFor(minutesUntilClose);
  } else {
    tier = 'green';
  }

  return {
    ...stop,
    originalEtaMs: stop.plannedStartMs,
    currentEtaMs,
    originalEtaLabel: formatDataTime(new Date(stop.plannedStartMs)),
    currentEtaLabel: formatDataTime(new Date(currentEtaMs)),
    riskTier: tier,
    riskReason: reasonForClosure({
      minutesUntilClose,
      delayMin: carryForwardDelayMin,
    }),
  };
}

// The driver-action events recorded at a route row's location. DriverActions
// records the suffixed location id in practice; the bare id is the fallback
// the rest of this file also accepts (mirrors matchTrafficLegs).
function eventsAtLocation(scansByLocationId, locationId) {
  if (!scansByLocationId || !locationId) {
    return [];
  }
  return (
    scansByLocationId[locationId] ||
    scansByLocationId[stripStopSuffix(locationId)] ||
    []
  );
}

// The driver-action events recorded at one stop, by type. A location can sit
// on multiple routes, so both pickers prefer entries whose driver matches
// this route's driver and fall back to all of them when none match (e.g. a
// completed route with no live ping).
const timedEventsOfType = (events, eventType) =>
  (events || []).filter(
    (event) => event.eventType === eventType && event.scannedAtMs != null,
  );

function preferRouteDriver(events, driverEmail) {
  const matching = driverEmail
    ? events.filter((event) => event.driver === driverEmail)
    : [];
  return matching.length > 0 ? matching : events;
}

// The arrival scan that led to a stocking: the latest PackingSlipQuery at or
// before the stocking time (mirrors ViewStockingDashboard's "latest matching
// scan"). Arrivals ONLY — the same map carries the LocationStocked event,
// stamped at the stocking moment, which would otherwise always win this test
// and collapse every on-site duration to zero. Null when no arrival
// qualifies, so the duration stays hidden rather than wrong.
function pickArrivalScan(events, driverEmail, stockedAtMs) {
  const arrivals = timedEventsOfType(events, ARRIVAL_SCAN_EVENT);
  const beforeStocking = preferRouteDriver(arrivals, driverEmail).filter(
    (arrival) => arrival.scannedAtMs <= stockedAtMs,
  );
  if (beforeStocking.length === 0) {
    return null;
  }
  return beforeStocking.reduce((latest, arrival) =>
    arrival.scannedAtMs > latest.scannedAtMs ? arrival : latest,
  );
}

// The LocationStocked event for a stocked stop — the most direct claim on
// who completed it. Matched by proximity to the row's stocking time rather
// than an at/before cutoff: the event and the row are written by different
// systems and can differ by a few seconds either way. Null without one.
function pickStockingEvent(events, driverEmail, stockedAtMs) {
  const stockings = timedEventsOfType(events, LOCATION_STOCKED_EVENT);
  if (stockings.length === 0) {
    return null;
  }
  const distanceToStocking = (event) =>
    Math.abs(event.scannedAtMs - stockedAtMs);
  return preferRouteDriver(stockings, driverEmail).reduce((closest, event) =>
    distanceToStocking(event) < distanceToStocking(closest) ? event : closest,
  );
}

function buildRow(stop, total) {
  return {
    state: stop.state,
    positionLabel: `Stop ${stop.stopIndex}/${total}`,
    stopName: stop.stopName,
    stopCode: stop.stopId,
    timeLabel: stop.stockedAt
      ? `Stocked ${stop.stockedAt}`
      : stop.etaLabel || '',
    durationLabel: '',
    deltaLabel: formatDeltaLabel(stop.delta),
    deltaTier: deltaTierFor(stop.delta),
  };
}

// The collapsed card timeline: the last stop done, the stop the driver is
// at or driving to, and the next stop still owed after that. `nextStop` is
// the in-flight leg destination for a driver who has not scanned in yet.
function buildTimeline(routeStops, nextStop) {
  // Rows resolve by state, not array position: when a driver skips ahead,
  // the current stop's sorted predecessor is pending, not the last done.
  const sorted = [...routeStops].sort(byStopIndex);
  const total = sorted.length;

  const prev = sorted
    .filter((s) => s.state === 'done')
    .reduce(
      (latest, s) =>
        !latest ||
        (s.stockedAtMs ?? -Infinity) >= (latest.stockedAtMs ?? -Infinity)
          ? s
          : latest,
      null,
    );
  const heading = sorted.find((s) => s.state === 'current') || nextStop;
  const next =
    sorted.find((s) => s.state === 'pending' && s !== heading) || null;

  const rows = [];
  if (prev) rows.push({ ...buildRow(prev, total), state: 'done' });
  if (heading) rows.push(buildRow(heading, total));
  if (next) rows.push({ ...buildRow(next, total), state: 'pending' });
  return rows;
}

function buildFullTimeline(routeStops) {
  const sorted = [...routeStops].sort(byStopIndex);
  const total = sorted.length;
  return sorted.map((stop) => buildRow(stop, total));
}

function buildOpsLocationsIndex(opsLocations) {
  const idx = {};
  (opsLocations || []).forEach((loc) => {
    if (loc?.id) idx[loc.id] = loc;
  });
  return idx;
}

// Reduce a route's raw DriverLocation history to the plottable, chronological
// trail the map draws as the driven path. A ping with no usable position
// would break the polyline and one with no usable timestamp would scramble
// its order, so both are dropped rather than guessed at.
export function buildPingTrail(trailPings) {
  return (trailPings || [])
    .map((ping) => {
      if (!ping) {
        return null;
      }
      const recordedAt = parseUtcDateTime(ping.timestamp);
      if (!recordedAt) {
        return null;
      }
      if (!Number.isFinite(ping.latitude) || !Number.isFinite(ping.longitude)) {
        return null;
      }
      return {
        latitude: ping.latitude,
        longitude: ping.longitude,
        timestampMs: recordedAt.getTime(),
        driverId: ping.driverId || null,
        driverEmail: ping.driverEmail || null,
        // The reading's own route stamp — a mis-stamped or unstamped ping
        // shows as such on the trail hover card.
        route: ping.route || null,
        // Telemetry the trail hover card reads; not every ping reports it.
        batteryLevel: Number.isFinite(ping.batteryLevel)
          ? ping.batteryLevel
          : null,
      };
    })
    .filter(Boolean)
    .sort((pointA, pointB) => pointA.timestampMs - pointB.timestampMs);
}

/**
 * Compose DriverRecord[] from raw inputs.
 *
 * @param {object} args
 * @param {Array} args.locationRoutes  Rows from getLocationRoutes (one per stop)
 * @param {Array} args.opsLocations    Rows from opsLocations (one per location)
 * @param {Array} args.driverPings     Latest ping per route from getDriverLocations
 * @param {Object} [args.scansByLocationId]
 *        Optional { [locationId]: [{ eventType, scannedAtMs, driver }] } map of
 *        driver-action events (PackingSlipQuery arrival scans and
 *        LocationStocked stockings). When provided, each completed stop gets
 *        actual on-site duration (stocked − scanned) from its arrival and is
 *        credited to its stocking's driver, disambiguated by route driver.
 * @param {Object} [args.trafficLegsByRoute]
 *        Optional { [route]: TrafficAwareDuration[] } map of driver-app
 *        drive legs. Adds per-stop drive fields, a measured current-stop
 *        ETA, and trafficDelayTodayMin.
 * @param {Object} [args.pingTrailsByRoute]
 *        Optional { [route]: DriverLocation[] } map of the route's FULL ping
 *        history for the day (not just the latest ping). Becomes each
 *        record's `pingTrail` — the driven path the single-route map draws.
 * @param {number} [args.nowMs]        Override now() — for tests
 * @returns {Array} DriverRecord[]
 */
export default function composeDriverRecords({
  locationRoutes = [],
  opsLocations = [],
  driverPings = [],
  scansByLocationId = null,
  trafficLegsByRoute = null,
  pingTrailsByRoute = null,
  nowMs = Date.now(),
} = {}) {
  if (!Array.isArray(locationRoutes) || locationRoutes.length === 0) return [];

  const opsIndex = buildOpsLocationsIndex(opsLocations);
  // Both DriverLocation and LocationRoute expose the friendly route name
  // on `route` (same field name on both types). Index pings under that
  // value so we can join them with LocationRoutes rows below.
  const pingByRouteId = {};
  (driverPings || []).forEach((p) => {
    if (p?.route) pingByRouteId[p.route] = p;
  });

  // Group LocationRoutes rows by `route` (the friendly name).
  const rowsByRouteId = {};
  locationRoutes.forEach((row) => {
    if (!row?.route) return;
    if (!rowsByRouteId[row.route]) rowsByRouteId[row.route] = [];
    rowsByRouteId[row.route].push(row);
  });

  return Object.entries(rowsByRouteId).map(([route, rows]) =>
    composeRouteRecord({
      route,
      rows,
      ping: pingByRouteId[route] || null,
      opsIndex,
      scansByLocationId,
      trafficLegs: trafficLegsByRoute ? trafficLegsByRoute[route] : null,
      trailPings: pingTrailsByRoute ? pingTrailsByRoute[route] : null,
      nowMs,
    }),
  );
}

// Flag out-of-sequence visits. `stops` is sorted ascending by stop number, so
// a done stop is "out of sequence" when any lower-numbered stop (an earlier
// index in the array) was either not done before it or stocked later than it
// — i.e. the driver jumped ahead of an earlier stop. Judged per driver: on a
// split route the other driver's timing says nothing about this driver's
// order, so lower-numbered stops attributed to someone else are skipped
// (unattributed stops still count). Returns a new array.
function markOutOfSequence(stops) {
  return stops.map((stop, k) => {
    if (stop.state !== 'done') return stop;
    const t = stop.stockedAtMs;
    const outOfSequence = stops.slice(0, k).some((prev) => {
      if (
        prev.stockedBy &&
        stop.stockedBy &&
        prev.stockedBy !== stop.stockedBy
      ) {
        return false;
      }
      if (prev.state !== 'done') return true;
      if (t === null || prev.stockedAtMs === null) return false;
      return prev.stockedAtMs > t;
    });
    return outOfSequence === stop.outOfSequence
      ? stop
      : { ...stop, outOfSequence };
  });
}

// A pending stop the route has already moved past: the driver reached a
// higher-numbered stop (stocked, or in flight toward it) without stocking
// this one. The backend has no skipped status, so this is derived — and
// provisional on a live route: a driver who circles back stocks the stop and
// the flag drops. On a completed route it is definitive. Returns the same
// array when nothing changed.
function markSkipped(stops) {
  const reachedIndexes = stops
    .filter((stop) => stop.state !== 'pending')
    .map((stop) => stop.stopIndex || 0);
  const furthestReachedIndex = Math.max(-Infinity, ...reachedIndexes);
  return stops.map((stop) => {
    const skipped =
      stop.state === 'pending' && (stop.stopIndex || 0) < furthestReachedIndex;
    return skipped === stop.skipped ? stop : { ...stop, skipped };
  });
}

// The state a stop is SHOWN in: the composer's state, except that a pending
// stop flagged as skipped reads 'skipped'. One rule for every surface — map
// disc, popup tag, lists — so they never disagree.
export function stopDisplayState(stop) {
  return stop.state === 'pending' && stop.skipped ? 'skipped' : stop.state;
}

// Distinct drivers for a list of emails, deduplicated case-insensitively and
// kept in first-appearance order; blanks are dropped. Each entry keeps the
// email beside its display name: the email is the stable identity, and a
// display name is lossy (two people can format to the same name).
function uniqueDriverRoster(emails) {
  const seen = new Set();
  const roster = [];
  emails.forEach((email) => {
    const normalized = (email || '').toLowerCase();
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      roster.push({ email: normalized, name: formatDriverName(normalized) });
    }
  });
  return roster;
}

// The drivers with DriverActions evidence on this route: a scan or stocking
// at one of its locations, or a traffic leg driven on it. A GPS ping is NOT
// evidence — phones stamp every ping with whatever route is selected in the
// app, so a driver who merely opened the app or toggled routes at dispatch
// would otherwise attach themselves to a route they never touched.
//
// Returns a Set of lower-cased emails, or null when there is nothing to
// judge by: `scansByLocationId` is null when the DriverActions fetch failed
// with no cached fallback, and null here means "skip the filter", so a
// transient backend failure never blanks out a working fleet. An empty but
// present map means the absence of events is real.
function creditedDriverEmails(rows, scansByLocationId, trafficLegs) {
  if (!scansByLocationId) {
    return null;
  }
  const credited = new Set();
  rows.forEach((row) => {
    eventsAtLocation(scansByLocationId, row?.id).forEach((event) => {
      if (event?.driver) {
        credited.add(event.driver.toLowerCase());
      }
    });
  });
  // Legs are already route-scoped, and cover a driver mid-drive who has
  // stocked nothing yet.
  (trafficLegs || []).forEach((leg) => {
    if (leg?.driver) {
      credited.add(leg.driver.toLowerCase());
    }
  });
  return credited;
}

// The stop a driver has physically scanned into but not finished stocking:
// an arrival scan on a row with no stockedTimeUTC. That scan is the only
// evidence anyone is actually AT a stop, so it alone makes a stop "current"
// ("In progress"). A drive leg means they are en route (see
// inFlightStopNumber), and the lowest unstocked number means nothing at all.
// The latest scan wins if two unstocked stops somehow carry one. Returns
// that stop's number, or null.
function scannedInStopNumber(sortedRows, scansByLocationId) {
  let latest = null;
  sortedRows.forEach((row) => {
    if (!row?.id || row.stockedTimeUTC) {
      return;
    }
    timedEventsOfType(
      eventsAtLocation(scansByLocationId, row.id),
      ARRIVAL_SCAN_EVENT,
    ).forEach((arrival) => {
      if (!latest || arrival.scannedAtMs > latest.scannedAtMs) {
        latest = { scannedAtMs: arrival.scannedAtMs, stop: row.stop };
      }
    });
  });
  return latest ? latest.stop : null;
}

// Rank each done stop by the time it was actually stocked (1 = first visit).
// Untimed done stops rank after timed ones, in planned order, so a route with
// patchy stockedTimeUTC data still numbers every visited stop. Non-done stops
// keep visitOrder null. Returns a new array.
function assignVisitOrder(stops) {
  const ranked = stops
    .filter((stop) => stop.state === 'done')
    .sort((stopA, stopB) => {
      const stockedAtMsA = stopA.stockedAtMs ?? Infinity;
      const stockedAtMsB = stopB.stockedAtMs ?? Infinity;
      return stockedAtMsA === stockedAtMsB
        ? byStopIndex(stopA, stopB)
        : stockedAtMsA - stockedAtMsB;
    });
  const rankOf = new Map(ranked.map((stop, index) => [stop, index + 1]));
  return stops.map((stop) =>
    stop.state === 'done' ? { ...stop, visitOrder: rankOf.get(stop) } : stop,
  );
}

// Pair each completed stop with its driver-action events: the arrival scan
// gives the actual on-site duration (stocked − scanned) and the stocking
// event says who completed the stop, the scanner being the fallback (usually
// the same person; the stocking is the stronger claim). Events are
// disambiguated by the route's driver email; a stop with no arrival simply
// leaves its duration unset. Returns a new array.
function attachScans(stops, scansByLocationId, driverEmail) {
  return stops.map((stop) => {
    const events = eventsAtLocation(scansByLocationId, stop.stopId);
    // The stop in flight is the one the driver scanned into and has not
    // finished: show the arrival that says so. No duration yet — they are
    // still there.
    if (stop.state === 'current') {
      const arrivals = preferRouteDriver(
        timedEventsOfType(events, ARRIVAL_SCAN_EVENT),
        driverEmail,
      );
      if (arrivals.length === 0) {
        return stop;
      }
      const arrival = arrivals.reduce((latest, event) =>
        event.scannedAtMs > latest.scannedAtMs ? event : latest,
      );
      return {
        ...stop,
        scannedAtMs: arrival.scannedAtMs,
        scannedAt: formatDataTime(new Date(arrival.scannedAtMs)),
        scannedBy: arrival.driver || null,
      };
    }
    if (stop.state !== 'done' || stop.stockedAtMs === null) {
      return stop;
    }
    const arrival = pickArrivalScan(events, driverEmail, stop.stockedAtMs);
    const stocking = pickStockingEvent(events, driverEmail, stop.stockedAtMs);
    const stockedBy = stocking?.driver || arrival?.driver || null;
    if (!arrival && !stockedBy) {
      return stop;
    }
    const withEvents = { ...stop };
    if (arrival) {
      withEvents.scannedAtMs = arrival.scannedAtMs;
      withEvents.scannedAt = formatDataTime(new Date(arrival.scannedAtMs));
      withEvents.actualDurationMin = Math.round(
        (stop.stockedAtMs - arrival.scannedAtMs) / 60000,
      );
    }
    if (stockedBy) {
      withEvents.stockedBy = stockedBy;
      withEvents.stockedByName = formatDriverName(stockedBy);
    }
    return withEvents;
  });
}

// Strip the "#n" route-occurrence suffix ("AMAZON_MDW9#1" → "AMAZON_MDW9").
export function stripStopSuffix(id) {
  return typeof id === 'string' ? id.replace(/#\d+$/, '') : id;
}

// Latest stocking time on the route (ms), or null when nothing is stocked.
// A leg written before it is stale: the driver has completed a stop since
// departing, so that leg no longer describes where they're heading.
function latestStockedMs(rows) {
  return rows.reduce((max, row) => {
    const t = parseUtcDateTime(row?.stockedTimeUTC);
    if (!t) return max;
    return max === null || t.getTime() > max ? t.getTime() : max;
  }, null);
}

// Pick each stop's traffic leg: join by destination LOCATION id — never by
// originStop/destinationStop sequence numbers, since drivers go out of
// sequence. A done stop takes the latest leg written before it was stocked
// (the drive that led to that visit); an unstocked stop takes the latest
// leg written since the route's last stocking — anything older is a drive
// the driver abandoned. Two visits of one location (the backend strips the
// #stockingIndex suffix, so they share an id) therefore resolve to different
// legs. A suffix-stripped id match is the fallback. Returns one
// { leg, departedAtMs } | null per stop, aligned with `stops`.
function matchTrafficLegs(stops, trafficLegs, lastStockedMs) {
  const entriesByKey = {};
  (trafficLegs || []).forEach((leg) => {
    if (!leg?.destination) return;
    const departed = parseUtcDateTime(leg.createdAt);
    if (!departed) return;
    const entry = { leg, departedAtMs: departed.getTime() };
    new Set([leg.destination, stripStopSuffix(leg.destination)]).forEach(
      (key) => {
        if (!entriesByKey[key]) entriesByKey[key] = [];
        entriesByKey[key].push(entry);
      },
    );
  });
  return stops.map((stop) => {
    const candidates =
      entriesByKey[stop.stopId] ||
      entriesByKey[stripStopSuffix(stop.stopId)] ||
      [];
    const isEligible =
      stop.state === 'done'
        ? (e) => stop.stockedAtMs === null || e.departedAtMs <= stop.stockedAtMs
        : (e) => lastStockedMs === null || e.departedAtMs >= lastStockedMs;
    return candidates
      .filter(isEligible)
      .reduce(
        (latest, e) =>
          !latest || e.departedAtMs > latest.departedAtMs ? e : latest,
        null,
      );
  });
}

// Attach display fields from each stop's matched leg (`legEntries` is
// aligned with `stops`). Returns a new array.
function attachTrafficLegs(stops, legEntries) {
  return stops.map((stop, i) => {
    const entry = legEntries[i];
    if (!entry) return stop;
    const { leg, departedAtMs } = entry;
    const legEtaMs = departedAtMs + leg.durationSeconds * 1000;
    return {
      ...stop,
      driveDurationMin: Math.round(leg.durationSeconds / 60),
      staticDurationMin: Math.round(leg.staticDurationSeconds / 60),
      trafficDelayMin: Math.round(leg.trafficDelaySeconds / 60),
      distanceMiles: leg.distanceMiles,
      legDepartedAtMs: departedAtMs,
      legEtaMs,
      legEtaLabel: formatDataTime(new Date(legEtaMs)),
      legOrigin: leg.origin || null,
      legPolyline: leg.polyline || null,
      legDriver: leg.driver || null,
    };
  });
}

// Attribute each done stop to the driver on its legs — the leg that arrived
// at it, else the first leg that departed from it after stocking (the first
// stop of a driver's chain has no arrival leg). Legs are written by the
// device that drove the hop, so they beat the arrival-scan attribution.
// Returns a new array.
function attributeLegDrivers(stops, trafficLegs) {
  const departuresByOrigin = {};
  (trafficLegs || []).forEach((leg) => {
    if (!leg?.driver || !leg?.origin) return;
    const departed = parseUtcDateTime(leg.createdAt);
    if (!departed) return;
    const key = stripStopSuffix(leg.origin);
    if (!departuresByOrigin[key]) departuresByOrigin[key] = [];
    departuresByOrigin[key].push({
      driver: leg.driver,
      departedAtMs: departed.getTime(),
    });
  });
  return stops.map((stop) => {
    if (stop.state !== 'done') return stop;
    let driver = stop.legDriver;
    if (!driver) {
      const departure = (departuresByOrigin[stripStopSuffix(stop.stopId)] || [])
        .filter(
          (d) =>
            stop.stockedAtMs === null || d.departedAtMs >= stop.stockedAtMs,
        )
        .sort((a, b) => a.departedAtMs - b.departedAtMs)[0];
      driver = departure ? departure.driver : null;
    }
    if (!driver) return stop;
    const email = driver.toLowerCase();
    return {
      ...stop,
      stockedBy: email,
      stockedByName: formatDriverName(email),
    };
  });
}

// The newest leg pointing at a still-unstocked stop is the driver's actual
// next destination (the driver app writes a leg at departure), so it beats
// the lowest-unstocked-number inference when a driver skips ahead. Legs
// older than the route's last stocking are ignored: the driver has stocked
// something since, so that departure was abandoned (e.g. a skipped stop) and
// must not pin the current stop or feed a past ETA into the forecast.
// Returns that stop's number, or null when no leg qualifies.
function inFlightStopNumber(trafficLegs, sortedRows, lastStockedMs) {
  if (!Array.isArray(trafficLegs) || trafficLegs.length === 0) return null;
  const unstockedStopByKey = {};
  sortedRows.forEach((row) => {
    if (!row?.id || row.stockedTimeUTC) return;
    const stripped = stripStopSuffix(row.id);
    if (unstockedStopByKey[row.id] === undefined) {
      unstockedStopByKey[row.id] = row.stop;
    }
    if (unstockedStopByKey[stripped] === undefined) {
      unstockedStopByKey[stripped] = row.stop;
    }
  });
  let best = null;
  trafficLegs.forEach((leg) => {
    if (!leg?.destination) return;
    const stop =
      unstockedStopByKey[leg.destination] ??
      unstockedStopByKey[stripStopSuffix(leg.destination)];
    if (stop === undefined) return;
    const departed = parseUtcDateTime(leg.createdAt);
    if (!departed) return;
    const departedAtMs = departed.getTime();
    if (lastStockedMs !== null && departedAtMs < lastStockedMs) return;
    if (!best || departedAtMs > best.departedAtMs) {
      best = { departedAtMs, stop };
    }
  });
  return best ? best.stop : null;
}

// Compose one DriverRecord for a route's rows: state assignment, stop passes
// (out-of-sequence, scans, traffic legs, forecast), status decision, and
// card labels.
function composeRouteRecord({
  route,
  rows,
  ping: latestPing,
  opsIndex,
  scansByLocationId,
  trafficLegs,
  trailPings,
  nowMs,
}) {
  const sortedRows = [...rows].sort((a, b) => (a.stop || 0) - (b.stop || 0));

  // Only drivers with evidence on this route count on it. With no evidence
  // set to judge by (fetch failed), everyone passes — see creditedDriverEmails.
  const credited = creditedDriverEmails(
    sortedRows,
    scansByLocationId,
    trafficLegs,
  );
  const isCredited = (email) =>
    !credited || (Boolean(email) && credited.has(email.toLowerCase()));

  // A ping is this route's ping only when its driver has evidence here.
  // Everything downstream reads it — the live marker's coordinates, the route
  // status, the driver roster — so gating it once covers them all.
  const ping = isCredited(latestPing?.driverEmail) ? latestPing : null;

  const lastStockedMs = latestStockedMs(sortedRows);
  // "Current" means the driver has scanned in and not finished stocking —
  // nothing else (see scannedInStopNumber). The newest in-flight leg names
  // the stop they are driving TO: still pending, but the map points at it.
  const currentStopNumber = scannedInStopNumber(sortedRows, scansByLocationId);
  const inFlightStop = inFlightStopNumber(
    trafficLegs,
    sortedRows,
    lastStockedMs,
  );
  const stopsCompleted = sortedRows.filter((r) => !!r.stockedTimeUTC).length;
  // A route is underway once something has actually happened on it: a stop
  // stocked, a credited live ping, or a leg in flight. Until then it is
  // not-started and no stop is current.
  const isUnderway =
    stopsCompleted > 0 || Boolean(ping) || inFlightStop !== null;

  // Build routeStops with state assignment.
  let routeStops = sortedRows.map((row) => {
    const opsLoc = opsIndex[row.id];
    let stateOverride = null;
    if (row.stockedTimeUTC) stateOverride = 'done';
    else if (row.stop === currentStopNumber) stateOverride = 'current';
    else stateOverride = 'pending';
    return buildRouteStop(row, opsLoc, stateOverride, nowMs);
  });

  if (scansByLocationId) {
    const driverEmail = (ping?.driverEmail || '').toLowerCase();
    routeStops = attachScans(routeStops, scansByLocationId, driverEmail);
  }

  // "Traffic delay today" sums the matched legs' RAW seconds (rounded once)
  // so per-leg rounding doesn't accumulate; the Set dedupes a leg that
  // qualifies for two stops sharing a location.
  let trafficDelayTodayMin = null;
  if (Array.isArray(trafficLegs) && trafficLegs.length > 0) {
    const legEntries = matchTrafficLegs(routeStops, trafficLegs, lastStockedMs);
    const matchedLegs = new Set(
      legEntries.filter(Boolean).map((entry) => entry.leg),
    );
    if (matchedLegs.size > 0) {
      routeStops = attachTrafficLegs(routeStops, legEntries);
      let totalDelaySec = 0;
      matchedLegs.forEach((leg) => {
        totalDelaySec += leg.trafficDelaySeconds || 0;
      });
      trafficDelayTodayMin = Math.round(totalDelaySec / 60);
    }
    routeStops = attributeLegDrivers(routeStops, trafficLegs);
  }

  // After attribution so the sequence check can be judged per driver.
  routeStops = markOutOfSequence(routeStops);
  routeStops = markSkipped(routeStops);

  routeStops = assignVisitOrder(routeStops);

  const stopsTotal = sortedRows.length;
  const lastDone = [...routeStops].reverse().find((s) => s.state === 'done');
  const currentStop = routeStops.find((s) => s.state === 'current');
  const nextStop =
    inFlightStop === null
      ? null
      : routeStops.find(
          (stop) => stop.stopIndex === inFlightStop && stop.state === 'pending',
        ) || null;

  // ETA delta: prefer the latest done stop's delta (how late the driver
  // came in on the most recent completed stop). Falls back to current-stop
  // delta (how late they are arriving) if nothing's done yet.
  let etaDeltaMin = null;
  if (lastDone && lastDone.delta !== null && lastDone.delta !== undefined) {
    etaDeltaMin = lastDone.delta;
  } else if (currentStop && currentStop.delta !== null) {
    etaDeltaMin = currentStop.delta;
  }

  // Status precedence — decided by stocking progress first, ping second:
  //   completed   — every stop stocked. Independent of ping: a finished route
  //                 whose GPS pings have aged out of the poll window (or a
  //                 past-date view) still has no live ping but is plainly done.
  //   not-started — nothing has happened on it yet: nothing stocked, no
  //                 credited ping, no leg in flight (truly pre-dispatch).
  //   else        — active: tier by schedule.
  // (Old logic checked `!ping` first, so any route without a current ping —
  // including fully-completed ones — was mislabeled "not-started".)
  let status;
  if (stopsTotal > 0 && stopsCompleted === stopsTotal) status = 'completed';
  else if (!isUnderway) status = 'not-started';
  else status = statusTierFor(etaDeltaMin);

  // Carry-forward forecast for the under-map ETA risk list. Only meaningful
  // for active routes — not-started routes have no measured delay yet, and
  // completed routes have no pending stops.
  if (status !== 'not-started' && status !== 'completed') {
    let carryForward = etaDeltaMin == null ? 0 : etaDeltaMin;
    // The in-flight leg's ETA is MEASURED (departure + live drive time) —
    // prefer its schedule delta over the stocked-history carry-forward,
    // whether the driver is still driving to that stop or already at it.
    const legStop = currentStop || nextStop;
    if (legStop?.legEtaMs != null && legStop.plannedStartMs != null) {
      carryForward = Math.round(
        (legStop.legEtaMs - legStop.plannedStartMs) / 60000,
      );
    }
    routeStops = routeStops.map((stop) => attachForecast(stop, carryForward));
  }

  const routeName = ping?.route || sortedRows[0]?.route || route;
  // Full stop-by-stop list is built for EVERY route state so the card can
  // show it (active, completed, not-started alike). The collapsed `timeline`
  // differs by state: active routes show prev/current/next; completed and
  // not-started routes have no "current" stop, so they show the first three
  // and expand to the full list.
  const fullTimeline = buildFullTimeline(routeStops);
  const timeline =
    status === 'not-started' || status === 'completed'
      ? fullTimeline.slice(0, 3)
      : buildTimeline(routeStops, nextStop);
  const remainingStopsCount = Math.max(0, stopsTotal - 3);

  // Planned departure label for not-started: earliest calculatedStartTime.
  let plannedDepartureLabel;
  if (status === 'not-started') {
    const firstPlanned = sortedRows
      .map((r) => parseUtcDateTime(r.calculatedStartTime))
      .filter(Boolean)
      .sort((a, b) => a - b)[0];
    plannedDepartureLabel = firstPlanned
      ? formatDataTime(firstPlanned)
      : undefined;
  }

  // backAtDepot label for completed: latest stockedTimeUTC.
  let backAtDepotLabel;
  if (status === 'completed') {
    const latestStocked = sortedRows
      .map((r) => parseUtcDateTime(r.stockedTimeUTC))
      .filter(Boolean)
      .sort((a, b) => b - a)[0];
    backAtDepotLabel = latestStocked
      ? formatDataTime(latestStocked)
      : undefined;
  }

  // Same evidence gate as the ping: a driver with no evidence on this route
  // contributes no trail to it either.
  const pingTrail = buildPingTrail(
    (trailPings || []).filter((point) => isCredited(point?.driverEmail)),
  );
  // Every driver who actually WORKED this route, in order of first
  // appearance: trail pings (chronological), then stop stockers the trail
  // missed, then the latest ping, then leg drivers — which catch a driver
  // mid-drive who has stocked nothing yet. Routes are sometimes shared, so
  // route-level surfaces show ALL of them rather than crediting whoever
  // pinged last — and the evidence gate keeps out drivers who only stamped a
  // ping here.
  const driverRoster = uniqueDriverRoster(
    [
      ...pingTrail.map((point) => point.driverEmail),
      ...routeStops.map((stop) => stop.stockedBy),
      ping?.driverEmail,
      ...(trafficLegs || []).map((leg) => leg?.driver),
    ].filter(isCredited),
  );

  return {
    driverId: ping?.driverId || `route-${route}`,
    driverEmail: ping?.driverEmail,
    // The latest-pinging driver — the phone the live marker tracks. Route
    // identity surfaces show `driverNames` instead.
    driverName: formatDriverName(ping?.driverEmail),
    driverNames: driverRoster.map((entry) => entry.name),
    // The same drivers keyed on email — the stable identity a driver picker
    // builds one-row-per-person options from.
    driverRoster,
    // The stop the driver is driving to (newest in-flight leg), or null. Not
    // "current" — that needs an arrival scan — but the map points at it.
    nextStopId: nextStop ? nextStop.stopId : null,
    // routeId here is the friendly route name (e.g. "il_chi_mon_am_loop_1"),
    // used as the stable join key throughout the FE. The DynamoDB UUID
    // routeId isn't on the GraphQL schema (see backend brief).
    routeId: route,
    routeName,
    status,
    etaDeltaMin: status === 'not-started' ? null : etaDeltaMin,
    stopsCompleted,
    stopsTotal,
    latitude: ping?.latitude,
    longitude: ping?.longitude,
    batteryLevel: ping?.batteryLevel,
    timestamp: ping?.timestamp,
    lastPingSecondsAgo: ping?.timestamp
      ? lastPingSecondsAgo(ping.timestamp, nowMs)
      : null,
    timeline,
    fullTimeline,
    remainingStopsCount,
    routeStops,
    pingTrail,
    trafficDelayTodayMin,
    plannedDepartureLabel,
    backAtDepotLabel,
  };
}
